import { alpha } from '@mui/material/styles';

// Colour that carries meaning without competing with the colour that carries alarm.
//
// Green, amber and red are spent entirely on operational status: something is right, slipping, or
// wrong. They are the colours an operator scans a screen for, and every decorative use of them
// makes that scan slower. So everything that is merely *categorical* — which stage a truck is at,
// which figure a card is showing — is coloured from the cool family instead, where nothing implies
// an alarm.
//
// A truck moves through the plant in a fixed order, so the stages move through the palette in that
// same order: waiting outside, at the gate, on the weighbridge, in the yard, away. The progression
// is the point. A truck at a weighbridge is not a warning, and should not look like one.
const STAGE = {
  CREATED: 'slate',
  YARD: 'slate',
  AT_YARD: 'slate',
  GATE_IN: 'blue',
  IN_PLANT: 'blue',
  WEIGHBRIDGE: 'teal',
  BAY: 'indigo',
  GATE_OUT: 'violet',
  IN_TRANSIT: 'violet',
  CLIENT: 'violet',
  CLOSED: 'neutral',
};

export const stageAccent = (stage) => STAGE[stage] || 'neutral';

const hue = (theme, key) => theme.palette.accents?.[key] || theme.palette.text.secondary;

// A badge reads as a label rather than a button: the hue at full strength for the text, the same
// hue at a tenth for the ground. The dark mode needs a stronger tint because a tenth of anything
// disappears against a dark surface.
export const accentChipSx = (key) => (theme) => {
  const main = hue(theme, key);
  const dark = theme.palette.mode === 'dark';
  return {
    color: main,
    backgroundColor: alpha(main, dark ? 0.18 : 0.1),
    borderColor: alpha(main, dark ? 0.42 : 0.3),
    borderWidth: 1,
    borderStyle: 'solid',
    fontWeight: 600,
  };
};

// A panel that belongs to one subject, tinted just enough to group it. Deliberately faint: this is
// meant to be felt rather than noticed, and a gate desk covered in colour is worse than a plain one.
export const tintedPanelSx = (key) => (theme) => {
  const main = hue(theme, key);
  const dark = theme.palette.mode === 'dark';
  return {
    backgroundColor: alpha(main, dark ? 0.09 : 0.05),
    borderColor: alpha(main, dark ? 0.32 : 0.22),
  };
};

// A figure on a dashboard, given an edge of its own colour so four cards in a row are four things
// rather than one wall.
export const accentCardSx = (key) => (theme) => {
  const main = hue(theme, key);
  const dark = theme.palette.mode === 'dark';
  return {
    borderColor: alpha(main, dark ? 0.32 : 0.22),
    backgroundColor: alpha(main, dark ? 0.08 : 0.045),
    borderLeft: `3px solid ${main}`,
  };
};

export const accentTextSx = (key) => (theme) => ({ color: hue(theme, key) });

// The order dashboard cards take their colours in, so the same card keeps the same colour.
export const CARD_ACCENTS = ['blue', 'teal', 'indigo', 'violet'];

// A sequential scale for the heatmap, which had been a single red ramp: red at every strength says
// "bad" even where nothing is wrong, and a cell holding the shortest dwell on the page should not
// look like an alarm.
//
// Both metrics are relative magnitudes, not SLA evaluations. Neither should borrow alarm colours.
const channels = (value) => [1, 3, 5].map((at) => parseInt(value.slice(at, at + 2), 16));
const between = (from, to, fraction) => channels(from)
  .map((start, i) => Math.round(start + (channels(to)[i] - start) * fraction));

export const rampColor = (theme, fraction, kind = 'duration') => {
  const stops = kind === 'count'
    ? [theme.palette.accents.teal, theme.palette.accents.blue, theme.palette.accents.indigo]
    : [theme.palette.accents.blue, theme.palette.accents.indigo, theme.palette.accents.violet];
  const clamped = Math.max(0, Math.min(1, fraction));
  const span = 1 / (stops.length - 1);
  const index = Math.min(stops.length - 2, Math.floor(clamped / span));
  const [r, g, b] = between(stops[index], stops[index + 1], (clamped - index * span) / span);
  // Weak cells stay pale so the table still reads as a table rather than a block of colour.
  const strength = (theme.palette.mode === 'dark' ? 0.12 : 0.08) + clamped * 0.22;
  return `rgba(${r}, ${g}, ${b}, ${strength.toFixed(3)})`;
};
